import { useEffect, useRef, useState } from 'react';

import { Button } from '@apps/web/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@apps/web/components/ui/dialog';
import { Input } from '@apps/web/components/ui/input';
import { Label } from '@apps/web/components/ui/label';
import { MultiSelect } from '@apps/web/components/ui/multi_select';
import { getApiClient } from '@apps/web/lib/api_client';
import { toast } from '@apps/web/lib/app_notifications';
import { normalizePhoneNumberToE164 } from '@apps/web/lib/phone_number_normalization';
import {
    updateMeOrgMemberProfile,
    useOrganizationOffices,
} from '@apps/web/swr_data_fetching/data_fetching_hooks';

type EditTeamMemberModalProps = {
    readonly open: boolean;
    readonly onOpenChange: (open: boolean) => void;
    readonly organizationId: string;
    readonly userId: string;
    readonly isSelf: boolean;
    readonly initialFirstName: string | null;
    readonly initialLastName: string | null;
    readonly initialPhoneNumber: string | null;
    readonly initialAssignedOfficeIds?: ReadonlyArray<string>;
    readonly canEditOfficeScope?: boolean;
    readonly onSaved: () => void | Promise<unknown>;
};

const EMPTY_ASSIGNED_OFFICE_IDS: ReadonlyArray<string> = [];

function sortOfficeIds(officeIds: ReadonlyArray<string>): Array<string> {
    return [...officeIds].sort((firstOfficeId, secondOfficeId) =>
        firstOfficeId.localeCompare(secondOfficeId),
    );
}

export function EditTeamMemberModal({
    open,
    onOpenChange,
    organizationId,
    userId,
    isSelf,
    initialFirstName,
    initialLastName,
    initialPhoneNumber,
    initialAssignedOfficeIds = EMPTY_ASSIGNED_OFFICE_IDS,
    canEditOfficeScope = false,
    onSaved,
}: EditTeamMemberModalProps) {
    const [firstName, setFirstName] = useState(initialFirstName ?? '');
    const [lastName, setLastName] = useState(initialLastName ?? '');
    const [phoneNumberInput, setPhoneNumberInput] = useState(
        initialPhoneNumber ?? '',
    );
    const [phoneError, setPhoneError] = useState<string | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [assignedOfficeIds, setAssignedOfficeIds] = useState<Array<string>>(
        sortOfficeIds(initialAssignedOfficeIds),
    );
    const initialAssignedOfficeIdsSnapshot = useRef(
        sortOfficeIds(initialAssignedOfficeIds),
    );
    const { data: offices } = useOrganizationOffices(
        canEditOfficeScope ? organizationId : null,
        {
            reason: 'edit-team-member-office-scope',
        },
    );
    const showOfficeScope = canEditOfficeScope && (offices?.length ?? 0) > 0;

    useEffect(() => {
        if (open) {
            setFirstName(initialFirstName ?? '');
            setLastName(initialLastName ?? '');
            setPhoneNumberInput(initialPhoneNumber ?? '');
            setPhoneError(null);
            const sortedInitialAssignedOfficeIds = sortOfficeIds(
                initialAssignedOfficeIds,
            );
            initialAssignedOfficeIdsSnapshot.current =
                sortedInitialAssignedOfficeIds;
            setAssignedOfficeIds(sortedInitialAssignedOfficeIds);
        }
    }, [
        open,
        initialFirstName,
        initialLastName,
        initialPhoneNumber,
        initialAssignedOfficeIds,
    ]);

    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault();

        const trimmedPhone = phoneNumberInput.trim();
        const normalizedPhoneNumber = trimmedPhone
            ? normalizePhoneNumberToE164(trimmedPhone)
            : null;
        if (trimmedPhone && normalizedPhoneNumber === null) {
            setPhoneError('Enter a valid phone number');
            return;
        }
        setPhoneError(null);

        const activeOfficeIds = new Set(
            offices?.map((office) => office.id) ?? [],
        );
        const sortedAssignedOfficeIds = sortOfficeIds(assignedOfficeIds);
        // Check the user's selection before sanitizing it so an empty or stale
        // office response cannot make an untouched assignment look changed.
        const assignedOfficeIdsChanged =
            sortedAssignedOfficeIds.length !==
                initialAssignedOfficeIdsSnapshot.current.length ||
            sortedAssignedOfficeIds.some(
                (officeId, index) =>
                    officeId !==
                    initialAssignedOfficeIdsSnapshot.current[index],
            );
        const sanitizedAssignedOfficeIds =
            canEditOfficeScope && offices && assignedOfficeIdsChanged
                ? sortedAssignedOfficeIds.filter((officeId) =>
                      activeOfficeIds.has(officeId),
                  )
                : undefined;

        setIsSubmitting(true);
        try {
            if (isSelf) {
                await getApiClient().patch({
                    url: '/users/me',
                    body: {
                        first_name: firstName.trim() || null,
                        last_name: lastName.trim() || null,
                    },
                });
                // The phone number here is this organization's SMS
                // notification number, so store it on the org membership —
                // members of several orgs can keep a different number per org.
                // Skip the write when unchanged so an untouched inherited
                // number is not pinned as an org override.
                const notificationPhoneNumberChanged =
                    normalizedPhoneNumber !== (initialPhoneNumber ?? null);
                if (
                    notificationPhoneNumberChanged ||
                    sanitizedAssignedOfficeIds !== undefined
                ) {
                    await updateMeOrgMemberProfile(organizationId, {
                        ...(notificationPhoneNumberChanged
                            ? {
                                  notification_phone_number:
                                      normalizedPhoneNumber,
                              }
                            : {}),
                        ...(sanitizedAssignedOfficeIds !== undefined
                            ? {
                                  assigned_office_ids:
                                      sanitizedAssignedOfficeIds,
                              }
                            : {}),
                    });
                }
            } else {
                await getApiClient().patch({
                    url: '/organizations/{organization_id}/users/{user_id}',
                    pathParams: {
                        organization_id: organizationId,
                        user_id: userId,
                    },
                    body: {
                        first_name: firstName.trim() || null,
                        last_name: lastName.trim() || null,
                        phone_number: normalizedPhoneNumber,
                        ...(sanitizedAssignedOfficeIds !== undefined
                            ? {
                                  assigned_office_ids:
                                      sanitizedAssignedOfficeIds,
                              }
                            : {}),
                    },
                });
            }
            toast.success('Team member updated', {
                persistNotification: true,
                notificationType: 'settings_update',
            });
            onOpenChange(false);
        } catch {
            toast.error('Failed to update team member', {
                persistNotification: true,
                notificationType: 'settings_update',
            });
            return;
        } finally {
            setIsSubmitting(false);
        }

        try {
            await onSaved();
        } catch {
            toast.error('Updated, but failed to refresh team members', {
                persistNotification: true,
                notificationType: 'settings_update',
            });
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-[425px]">
                <form onSubmit={handleSubmit}>
                    <DialogHeader>
                        <DialogTitle>Edit Team Member</DialogTitle>
                        <DialogDescription>
                            {showOfficeScope
                                ? 'Update this team member’s name, phone ' +
                                  'number, and office scope.'
                                : 'Update this team member’s name and ' +
                                  'phone number.'}
                        </DialogDescription>
                    </DialogHeader>
                    <div className="grid gap-4 py-4">
                        {showOfficeScope && offices && (
                            <div className="grid gap-2">
                                <Label>Assigned offices</Label>
                                <MultiSelect
                                    options={offices.map((office) => ({
                                        value: office.id,
                                        label: office.display_name,
                                    }))}
                                    selected={assignedOfficeIds}
                                    onChange={setAssignedOfficeIds}
                                    placeholder="All offices"
                                    ariaLabel="Assigned offices"
                                    matchTriggerWidth={true}
                                    portalWithinDialog={true}
                                />
                            </div>
                        )}
                        <div className="grid gap-2">
                            <Label htmlFor="edit-team-member-first-name">
                                First name
                            </Label>
                            <Input
                                id="edit-team-member-first-name"
                                type="text"
                                value={firstName}
                                onChange={(event) =>
                                    setFirstName(event.target.value)
                                }
                            />
                        </div>
                        <div className="grid gap-2">
                            <Label htmlFor="edit-team-member-last-name">
                                Last name
                            </Label>
                            <Input
                                id="edit-team-member-last-name"
                                type="text"
                                value={lastName}
                                onChange={(event) =>
                                    setLastName(event.target.value)
                                }
                            />
                        </div>
                        <div className="grid gap-2">
                            <Label htmlFor="edit-team-member-phone-number">
                                Phone number
                            </Label>
                            <Input
                                id="edit-team-member-phone-number"
                                type="tel"
                                autoComplete="tel"
                                placeholder="+1 (555) 123-4567"
                                value={phoneNumberInput}
                                aria-invalid={phoneError !== null}
                                onChange={(event) => {
                                    setPhoneNumberInput(event.target.value);
                                    setPhoneError(null);
                                }}
                            />
                            {phoneError ? (
                                <p className="text-sm text-red-500">
                                    {phoneError}
                                </p>
                            ) : null}
                        </div>
                    </div>
                    <DialogFooter>
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => onOpenChange(false)}
                            disabled={isSubmitting}
                        >
                            Cancel
                        </Button>
                        <Button type="submit" disabled={isSubmitting}>
                            {isSubmitting ? 'Saving…' : 'Save'}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}
